import React, { useState } from 'react';
import DepthPanel from '../../components/panels/log-viewer/depth-panel';
import ExportPanel from '../../components/panels/log-viewer/export-panel';
import InfoPanel from '../../components/panels/log-viewer/info-panel';
import PrintPanel from '../../components/panels/log-viewer/print-panel';
import TemplatesPanel from '../../components/panels/log-viewer/templates-panel';
import ViewsPanel from '../../components/panels/log-viewer/views-panel';
import XAxisPanel from '../../components/panels/log-viewer/x-axis-panel';
import SaveAsDialog from '../../components/dialogs/save-as-dialog';

interface LeftSidePanelProps {
  showLeftSidePanel: boolean;
  showDepthPanel: boolean;
  showXAxisPanel: boolean;
  showPrintPanel: boolean;
  showTemplatesPanel: boolean;
  showViewsPanel: boolean;
  showInfoPanel: boolean;
  showExportPanel: boolean;
  isViewMode: boolean;
  isMultiBoreholeMode: boolean;
  // DepthPanel props
  depthType: string;
  onChangeDepthType: (value: any) => void;
  depthUnit: string | null;
  changeDepthUnit: (value: any) => void;
  childLoading: boolean;
  setStartDepth: (value: number | null | undefined) => void;
  setEndDepth: (value: number | null | undefined) => void;
  minDepth: number | null;
  maxDepth: number | null;
  setSkipLoad: (value: boolean) => void;
  showDepthGridLines: boolean | undefined;
  majorIntervals: number | undefined;
  setMajorIntervals: (value: number | undefined) => void;
  setShowDepthGridlines: (value: boolean) => void;
  showDepthMinorIntervals: boolean | undefined;
  setShowDepthMinorIntervals: (value: boolean) => void;
  minorIntervals: number | undefined;
  setMinorIntervals: (value: number | undefined) => void;
  setManualDepthRange: (value: boolean) => void;
  startDepth: number | null;
  endDepth: number | null;
  hasLoggedDepth: boolean;
  // XAxisPanel props
  showValueAxisAnnotation: boolean | undefined;
  setShowValueAxisAnnotation: (value: boolean) => void;
  // TemplatesPanel props
  currentTemplate: any;
  currentTemplateLevel: string | null;
  moveTemplateLevel: (level: number) => void;
  moveTemplateError: string | null;
  builtInTemplateOptions: any[];
  userTemplateOptions: any[];
  boreholeTemplateOptions: any[];
  projectTemplateOptions: any[];
  clientTemplateOptions: any[];
  onChangeBuiltInTemplate: (template: any) => void;
  onChangeUserTemplate: (template: any) => void;
  onChangeBoreholeTemplate: (template: any) => void;
  onChangeProjectTemplate: (template: any) => void;
  onChangeClientTemplate: (template: any) => void;
  selectedTemplateId: string | null;
  templateLoading: boolean;
  onSaveTemplate: (name: string, level: string) => void;
  canSaveTemplate: boolean;
  canDeleteTemplate: boolean;
  templateNotSaved: boolean;
  templateSaveError?: any;
  onSaveTemplateAs: (name: string, level: string) => void;
  onDeleteTemplate: () => void;
  onNewTemplate: () => void;
  templateLevels: any[];
  mouseWheelZoom: boolean;
  setMouseWheelZoom: (zoom: boolean) => void;
  // ViewsPanel props
  currentView: any;
  currentViewLevel: string | null;
  moveViewLevel: (level: number) => void;
  moveViewError: string | null;
  viewsLoading: boolean;
  userViewOptions: any[];
  clientViewOptions: any[];
  onChangeUserView: (view: any) => void;
  onChangeClientView: (view: any) => void;
  selectedViewId: string | null;
  onSaveView: (name: string, level: string) => void;
  canSaveView: boolean;
  canDeleteView: boolean;
  viewNotSaved: boolean;
  onSaveViewAs: (name: string, level: string) => void;
  onNewView: () => void;
  // InfoPanel props
  metaDataDepth: any;
  latitude: number | null | undefined;
  longitude: number | null | undefined;
  loggingStarted: any;
  loggingEnded: any;
  loggingTeam: string | null | undefined;
  comments: string | null | undefined;
  status: string | null | undefined;
  equipment: string | null | undefined;
  infoImage: string | null | undefined;
  // PrintPanel props
  handleOkPrintModal: (
    size: number,
    orientation: number,
    pagesToPrintOver: number,
    pixelWidth: number | null,
    pixelHeight: number | null,
    fanFold: boolean,
    tracksPerPage: number,
    fixTracksPerPage: boolean,
    totalPages: number,
    fixTotalPages: boolean,
    startDepth: number,
    endDepth: number,
    depthPerPage: number,
    printOverPages: boolean,
    leftDepthScale: boolean,
    rightDepthScale: boolean,
    printStatisticalSummary: boolean,
    selectedDataForDepthRange: number[]
  ) => void;
  isPrinting: boolean;
  paperSizes: Array<{ value: number; name: string }>;
  printError: any;
  printProgress: any;
  showSummaryReport: boolean;
  statisticsTracks: any[];
  dataForDepthRangeOptions: any[];
  // ExportPanel props
  isExporting: boolean;
  setIsExporting: (value: boolean) => void;
  exportFile: (...args: any[]) => void;
  exportError: any;
  exportProgress: any;
  exportFileName: string;
  setExportFileName: (value: string) => void;
  exportDownloaded: boolean;
  exportUrl: string;
  showExportTemplates: boolean;
  // SaveAsDialog props
  showSaveAsModal: boolean;
  showSaveViewAsModal: boolean;
  saveTemplateName: string;
  setSaveTemplateName: (name: string) => void;
  saveTemplateNameError: any;
  saveAsTemplateLevel: any;
  setSaveAsTemplateLevel: (level: any) => void;
  saveViewName: string;
  setSaveViewName: (name: string) => void;
  saveViewNameError: any;
  saveAsViewLevel: any;
  setSaveAsViewLevel: (level: any) => void;
  onShowSaveTemplateDialog: () => void;
  onShowSaveViewDialog: () => void;
  handleCloseSaveAsModal: () => void;
  handleOkSaveAsModal: () => void;
  handleCloseSaveViewAsModal: () => void;
  handleOkSaveViewAsModal: () => void;
}

const LeftSidePanel: React.FC<LeftSidePanelProps> = ({
  showLeftSidePanel,
  showDepthPanel,
  showXAxisPanel,
  showPrintPanel,
  showTemplatesPanel,
  showViewsPanel,
  showInfoPanel,
  showExportPanel,
  isViewMode,
  isMultiBoreholeMode,
  depthType,
  onChangeDepthType,
  depthUnit,
  changeDepthUnit,
  childLoading,
  setStartDepth,
  setEndDepth,
  minDepth,
  maxDepth,
  setSkipLoad,
  showDepthGridLines,
  majorIntervals,
  setMajorIntervals,
  setShowDepthGridlines,
  showDepthMinorIntervals,
  setShowDepthMinorIntervals,
  minorIntervals,
  setMinorIntervals,
  setManualDepthRange,
  startDepth,
  endDepth,
  hasLoggedDepth,
  showValueAxisAnnotation,
  setShowValueAxisAnnotation,
  currentTemplate,
  currentTemplateLevel,
  moveTemplateLevel,
  moveTemplateError,
  builtInTemplateOptions,
  userTemplateOptions,
  boreholeTemplateOptions,
  projectTemplateOptions,
  clientTemplateOptions,
  onChangeBuiltInTemplate,
  onChangeUserTemplate,
  onChangeBoreholeTemplate,
  onChangeProjectTemplate,
  onChangeClientTemplate,
  selectedTemplateId,
  templateLoading,
  onSaveTemplate,
  canSaveTemplate,
  canDeleteTemplate,
  templateNotSaved,
  templateSaveError,
  onSaveTemplateAs,
  onDeleteTemplate,
  onNewTemplate,
  templateLevels,
  mouseWheelZoom,
  setMouseWheelZoom,
  currentView,
  currentViewLevel,
  moveViewLevel,
  moveViewError,
  viewsLoading,
  userViewOptions,
  clientViewOptions,
  onChangeUserView,
  onChangeClientView,
  selectedViewId,
  onSaveView,
  canSaveView,
  canDeleteView,
  viewNotSaved,
  onSaveViewAs,
  onNewView,
  metaDataDepth,
  latitude,
  longitude,
  loggingStarted,
  loggingEnded,
  loggingTeam,
  comments,
  status,
  equipment,
  infoImage,
  handleOkPrintModal,
  isPrinting,
  paperSizes,
  printError,
  printProgress,
  showSummaryReport,
  statisticsTracks,
  dataForDepthRangeOptions,
  isExporting,
  setIsExporting,
  exportFile,
  exportError,
  exportProgress,
  exportFileName,
  setExportFileName,
  exportDownloaded,
  exportUrl,
  showExportTemplates,
  showSaveAsModal,
  showSaveViewAsModal,
  saveTemplateName,
  setSaveTemplateName,
  saveTemplateNameError,
  saveAsTemplateLevel,
  setSaveAsTemplateLevel,
  saveViewName,
  setSaveViewName,
  saveViewNameError,
  saveAsViewLevel,
  setSaveAsViewLevel,
  onShowSaveTemplateDialog,
  onShowSaveViewDialog,
  handleCloseSaveAsModal,
  handleOkSaveAsModal,
  handleCloseSaveViewAsModal,
  handleOkSaveViewAsModal,
}) => {

  return (
    <>
      <div className="left-side-panel" style={{ width: showLeftSidePanel ? 300 : 0 }}>
        {showDepthPanel && (
          <DepthPanel
            depthType={depthType}
            onChangeDepthType={onChangeDepthType}
            depthUnit={depthUnit}
            changeDepthUnit={changeDepthUnit}
            childLoading={childLoading}
            setStartDepth={setStartDepth}
            setEndDepth={setEndDepth}
            minDepth={minDepth}
            maxDepth={maxDepth}
            setSkipLoad={setSkipLoad}
            showDepthGridLines={showDepthGridLines}
            majorIntervals={majorIntervals}
            setMajorIntervals={setMajorIntervals}
            setShowDepthGridlines={setShowDepthGridlines}
            showDepthMinorIntervals={showDepthMinorIntervals}
            setShowDepthMinorIntervals={setShowDepthMinorIntervals}
            minorIntervals={minorIntervals}
            setMinorIntervals={setMinorIntervals}
            setManualDepthRange={setManualDepthRange}
            startDepth={startDepth}
            endDepth={endDepth}
            setMouseWheelZoom={setMouseWheelZoom}
            mouseWheelZoom={mouseWheelZoom}
            hasLoggedDepth={hasLoggedDepth}
          />
        )}
        {showPrintPanel && (
          <PrintPanel
            childLoading={childLoading}
            handleOkPrintModal={handleOkPrintModal}
            isPrinting={isPrinting}
            paperSizes={paperSizes}
            startDepth={startDepth}
            endDepth={endDepth}
            minDepth={minDepth}
            maxDepth={maxDepth}
            depthUnit={depthUnit}
            error={printError}
            progress={printProgress}
            showSummaryReport={showSummaryReport}
            statisticsTracks={statisticsTracks}
            dataForDepthRangeOptions={dataForDepthRangeOptions}
          />
        )}
        {showTemplatesPanel && !isViewMode && !isMultiBoreholeMode && (
          <TemplatesPanel
            currentTemplate={currentTemplate}
            currentTemplateLevel={currentTemplateLevel}
            moveTemplateLevel={moveTemplateLevel}
            moveError={moveTemplateError}
            builtInTemplateOptions={builtInTemplateOptions}
            userTemplateOptions={userTemplateOptions}
            boreholeTemplateOptions={boreholeTemplateOptions}
            projectTemplateOptions={projectTemplateOptions}
            clientTemplateOptions={clientTemplateOptions}
            onChangeBuiltInTemplate={onChangeBuiltInTemplate}
            onChangeUserTemplate={onChangeUserTemplate}
            onChangeBoreholeTemplate={onChangeBoreholeTemplate}
            onChangeProjectTemplate={onChangeProjectTemplate}
            onChangeClientTemplate={onChangeClientTemplate}
            selectedTemplateId={selectedTemplateId}
            childLoading={templateLoading}
            onSave={onSaveTemplate}
            canSaveTemplate={canSaveTemplate}
            canDeleteTemplate={canDeleteTemplate}
            notSaved={templateNotSaved}
            saveError={templateSaveError}
            onSaveAs={onShowSaveTemplateDialog}
            deleteTemplate={onDeleteTemplate}
            onNewTemplate={onNewTemplate}
            templateLevels={templateLevels}
            mouseWheelZoom={mouseWheelZoom}
            setMouseWheelZoom={setMouseWheelZoom}
          />
        )}
        {showViewsPanel && (isViewMode || isMultiBoreholeMode) && (
          <ViewsPanel
            currentView={currentView}
            currentViewLevel={currentViewLevel}
            moveViewLevel={moveViewLevel}
            moveError={moveViewError}
            viewsLoading={viewsLoading}
            userViewOptions={userViewOptions}
            clientViewOptions={clientViewOptions}
            onChangeUserView={onChangeUserView}
            onChangeClientView={onChangeClientView}
            selectedViewId={selectedViewId}
            childLoading={viewsLoading}
            onSave={onSaveView}
            canSaveView={canSaveView}
            canDeleteView={canDeleteView}
            notSaved={viewNotSaved}
            onSaveAs={onShowSaveViewDialog}
            deleteView={() => {}}
            onNewView={onNewView}
            mouseWheelZoom={mouseWheelZoom}
            setMouseWheelZoom={setMouseWheelZoom}
          />
        )}
        {showInfoPanel && (
          <InfoPanel
            metaDataDepth={metaDataDepth}
            depthUnit={depthUnit}
            latitude={latitude}
            longitude={longitude}
            loggingStarted={loggingStarted}
            loggingEnded={loggingEnded}
            loggingTeam={loggingTeam}
            comments={comments}
            status={status}
            equipment={equipment}
            image={infoImage}
          />
        )}
        {showXAxisPanel && (
          <XAxisPanel
            showValueAxisAnnotation={showValueAxisAnnotation}
            setShowValueAxisAnnotation={setShowValueAxisAnnotation}
          />
        )}
        {showExportPanel && (
          <ExportPanel
            isExporting={isExporting}
            setIsExporting={setIsExporting}
            exportFile={exportFile}
            error={exportError}
            progress={exportProgress}
            exportFileName={exportFileName}
            setExportFileName={setExportFileName}
            downloaded={exportDownloaded}
            url={exportUrl}
            showTemplates={showExportTemplates}
          />
        )}
      </div>

      <SaveAsDialog
        showSaveAsModal={showSaveAsModal}
        handleCloseSaveAsModal={handleCloseSaveAsModal}
        handleOkSaveAsModal={handleOkSaveAsModal}
        onSaveAsFormSubmit={handleOkSaveAsModal}
        saveTemplateName={saveTemplateName}
        setSaveTemplateName={setSaveTemplateName}
        saveTemplateNameError={saveTemplateNameError}
        templateLevel={saveAsTemplateLevel}
        setTemplateLevel={setSaveAsTemplateLevel}
        forView={false}
      />
      <SaveAsDialog
        showSaveAsModal={showSaveViewAsModal}
        handleCloseSaveAsModal={handleCloseSaveViewAsModal}
        handleOkSaveAsModal={handleOkSaveViewAsModal}
        onSaveAsFormSubmit={handleOkSaveViewAsModal}
        saveTemplateName={saveViewName}
        setSaveTemplateName={setSaveViewName}
        saveTemplateNameError={saveViewNameError}
        templateLevel={saveAsViewLevel}
        setTemplateLevel={setSaveAsViewLevel}
        forView={true}
      />
    </>
  );
};

export default LeftSidePanel;
